import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Pencil, Plus, Tag, Trash2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { friendlyError } from "@/lib/errors";
import { useActiveOrg } from "@/features/tenancy/hooks";
import { Button } from "@/components/ui/Button";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { StatusPill } from "@/components/ui/StatusPill";
import { QueryBoundary } from "@/components/ui/QueryBoundary";
import { EmptyState } from "@/components/ui/states";
import { Field, FormGrid, Input, Select, Textarea, Toggle } from "@/components/ui/Form";
import { toast } from "@/components/ui/toast";
import {
  TIPOS,
  VACIO,
  aBorrador,
  createCoupon,
  deleteCoupon,
  fetchCoupons,
  porQueNoAplica,
  resumen,
  toggleCoupon,
  updateCoupon,
  validar,
  type Coupon,
  type CouponDraft,
} from "./api";

export function MarketingPage() {
  const { org } = useActiveOrg();
  const qc = useQueryClient();
  const orgId = org?.organizationId ?? null;
  const moneda = org?.currency ?? "PEN";

  const [editando, setEditando] = useState<Coupon | null>(null);
  const [creando, setCreando] = useState(false);
  const [borrando, setBorrando] = useState<Coupon | null>(null);

  const cupones = useQuery({
    queryKey: ["coupons", orgId],
    queryFn: () => fetchCoupons(orgId!),
    enabled: Boolean(orgId),
  });

  const refrescar = () => qc.invalidateQueries({ queryKey: ["coupons", orgId] });

  async function cambiarEstado(c: Coupon) {
    try {
      await toggleCoupon(c.id, !c.active);
      void refrescar();
      toast.ok(c.active ? "Cupón desactivado." : "Cupón activado.");
    } catch (error) {
      toast.error(friendlyError(error));
    }
  }

  async function copiar(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      toast.ok(`${code} copiado.`);
    } catch {
      toast.error("No se pudo copiar. Selecciónalo a mano.");
    }
  }

  async function eliminar() {
    if (!borrando) return;
    try {
      await deleteCoupon(borrando.id);
      void refrescar();
      toast.ok("Cupón eliminado.");
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setBorrando(null);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-head font-semibold tracking-tight">Marketing</h1>
          <p className="mt-0.5 text-meta text-ink-2">
            Cupones de descuento para tu tienda.
          </p>
        </div>
        <Button
          variant="primary"
          icon={<Plus className="size-3.5" />}
          onClick={() => setCreando(true)}
        >
          Nuevo cupón
        </Button>
      </header>

      <QueryBoundary
        query={cupones}
        isEmpty={(d) => d.length === 0}
        empty={
          <Panel>
            <EmptyState
              icon={<Tag className="size-5" />}
              title="Sin cupones"
              description="Crea uno y el comprador podrá escribirlo al pagar. El descuento lo calcula la tienda, no el navegador."
              actions={
                <Button size="sm" variant="primary" onClick={() => setCreando(true)}>
                  Crear el primero
                </Button>
              }
            />
          </Panel>
        }
      >
        {(lista) => (
          <div className="flex flex-col gap-2">
            {lista.map((c) => {
              const motivo = porQueNoAplica(c);
              const agotado = c.max_uses !== null && c.times_used >= c.max_uses;

              return (
                <Panel key={c.id}>
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <code
                          className={cn(
                            "rounded bg-sunk px-2 py-1 font-mono text-ui font-semibold tracking-wide",
                            motivo && "text-ink-3",
                          )}
                        >
                          {c.code}
                        </code>
                        <button
                          type="button"
                          onClick={() => void copiar(c.code)}
                          className="rounded p-1 text-ink-3 hover:bg-sunk hover:text-ink-2"
                          aria-label={`Copiar ${c.code}`}
                        >
                          <Copy className="size-3.5" />
                        </button>

                        <button type="button" onClick={() => void cambiarEstado(c)}>
                          <StatusPill tone={motivo ? "neutral" : "ok"}>
                            {motivo ?? "Activo"}
                          </StatusPill>
                        </button>
                      </div>

                      <p className="mt-1.5 text-ui">{resumen(c, moneda)}</p>
                      {c.description && (
                        <p className="mt-0.5 text-meta text-ink-2">{c.description}</p>
                      )}

                      <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-meta text-ink-3">
                        {Number(c.min_order) > 0 && (
                          <Dato k="Mínimo" v={`${moneda} ${Number(c.min_order).toFixed(2)}`} />
                        )}
                        <Dato
                          k="Usos"
                          v={c.max_uses === null ? `${c.times_used} · sin límite` : `${c.times_used} de ${c.max_uses}`}
                          alerta={agotado}
                        />
                        <Dato k="Por cliente" v={String(c.max_uses_per_customer)} />
                        {c.ends_at && (
                          <Dato
                            k="Vence"
                            v={new Date(c.ends_at).toLocaleDateString("es-PE", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })}
                          />
                        )}
                      </dl>
                    </div>

                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditando(c)}
                        className="rounded p-1.5 text-ink-2 hover:bg-sunk"
                        aria-label={`Editar ${c.code}`}
                      >
                        <Pencil className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setBorrando(c)}
                        className="rounded p-1.5 text-ink-2 hover:bg-sunk hover:text-crit"
                        aria-label={`Eliminar ${c.code}`}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>
                  </div>
                </Panel>
              );
            })}
          </div>
        )}
      </QueryBoundary>

      {(creando || editando) && (
        <CouponModal
          cupon={editando}
          organizationId={orgId!}
          moneda={moneda}
          onClose={() => {
            setCreando(false);
            setEditando(null);
          }}
          onSaved={() => {
            void refrescar();
            setCreando(false);
            setEditando(null);
          }}
        />
      )}

      {borrando && (
        <Modal
          open
          tone="danger"
          title={`Eliminar ${borrando.code}`}
          onClose={() => setBorrando(null)}
          description={
            borrando.times_used > 0 ? (
              <>
                Este cupón se usó <b className="text-ink">{borrando.times_used} vez(es)</b>.
                Al eliminarlo se pierde el registro de esos canjes; los pedidos conservan
                el código con el que se hicieron. Si solo quieres que deje de funcionar,
                desactívalo.
              </>
            ) : (
              <>Dejará de funcionar en el checkout. Nunca se usó, así que no se pierde
              ningún registro.</>
            )
          }
          footer={
            <>
              <Button size="sm" variant="secondary" onClick={() => setBorrando(null)}>
                Cancelar
              </Button>
              <Button size="sm" variant="danger" onClick={() => void eliminar()}>
                Eliminar
              </Button>
            </>
          }
        />
      )}
    </div>
  );
}

function Dato({ k, v, alerta }: { k: string; v: string; alerta?: boolean }) {
  return (
    <div className="flex gap-1">
      <dt>{k}:</dt>
      <dd className={cn("tnum font-medium", alerta ? "text-warn" : "text-ink-2")}>{v}</dd>
    </div>
  );
}

function CouponModal({
  cupon,
  organizationId,
  moneda,
  onClose,
  onSaved,
}: {
  cupon: Coupon | null;
  organizationId: string;
  moneda: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [b, setB] = useState<CouponDraft>(cupon ? aBorrador(cupon) : VACIO);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);

  const nuevo = !cupon;
  const set = <K extends keyof CouponDraft>(campo: K, valor: CouponDraft[K]) =>
    setB((x) => ({ ...x, [campo]: valor }));

  const tipo = TIPOS.find((t) => t.id === b.kind)!;

  async function guardar() {
    const fallos = validar(b, nuevo);
    setErrores(fallos);
    if (Object.keys(fallos).length > 0) return;

    setGuardando(true);
    try {
      if (cupon) await updateCoupon(cupon.id, organizationId, b);
      else await createCoupon(organizationId, b);
      toast.ok(cupon ? "Cupón actualizado." : "Cupón creado.");
      onSaved();
    } catch (error) {
      toast.error(friendlyError(error));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={cupon ? `Editar ${cupon.code}` : "Nuevo cupón"}
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button size="sm" variant="primary" onClick={() => void guardar()} loading={guardando}>
            {cupon ? "Guardar" : "Crear cupón"}
          </Button>
        </>
      }
    >
      <div className="flex max-h-[60vh] flex-col gap-3.5 overflow-y-auto pr-1">
        <Field
          label="Código"
          required
          hint={
            nuevo
              ? "Lo que el comprador escribe al pagar. No importan mayúsculas."
              : "El código no se cambia: los pedidos ya hechos lo guardan."
          }
          error={errores.code}
        >
          <Input
            value={b.code}
            onChange={(e) => set("code", e.target.value.toUpperCase())}
            placeholder="BIENVENIDA10"
            disabled={!nuevo}
            spellCheck={false}
            className="font-mono tracking-wide"
          />
        </Field>

        <Field label="Tipo de descuento" hint={tipo.ayuda}>
          <Select value={b.kind} onChange={(e) => set("kind", e.target.value as CouponDraft["kind"])}>
            {TIPOS.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </Select>
        </Field>

        {b.kind !== "free_shipping" && (
          <FormGrid>
            <Field
              label={b.kind === "percent" ? "Porcentaje" : `Monto (${moneda})`}
              required
              error={errores.value}
            >
              <Input
                inputMode="decimal"
                value={b.value}
                onChange={(e) => set("value", e.target.value)}
                placeholder={b.kind === "percent" ? "10" : "20.00"}
              />
            </Field>

            {b.kind === "percent" && (
              <Field
                label={`Tope del descuento (${moneda})`}
                hint="Sin tope, un 20% sobre un pedido grande regala mucho."
                error={errores.max_discount}
              >
                <Input
                  inputMode="decimal"
                  value={b.max_discount}
                  onChange={(e) => set("max_discount", e.target.value)}
                  placeholder="Sin tope"
                />
              </Field>
            )}
          </FormGrid>
        )}

        <Field
          label={`Compra mínima (${moneda})`}
          hint="Se mide sobre la mercadería, sin contar el envío."
          error={errores.min_order}
        >
          <Input
            inputMode="decimal"
            value={b.min_order}
            onChange={(e) => set("min_order", e.target.value)}
            placeholder="Sin mínimo"
          />
        </Field>

        <FormGrid>
          <Field label="Empieza" hint="Vacío = ya está activo.">
            <Input
              type="datetime-local"
              value={b.starts_at}
              onChange={(e) => set("starts_at", e.target.value)}
            />
          </Field>
          <Field label="Vence" hint="Vacío = no vence." error={errores.ends_at}>
            <Input
              type="datetime-local"
              value={b.ends_at}
              onChange={(e) => set("ends_at", e.target.value)}
            />
          </Field>
        </FormGrid>

        <FormGrid>
          <Field label="Usos en total" hint="Vacío = sin límite." error={errores.max_uses}>
            <Input
              inputMode="numeric"
              value={b.max_uses}
              onChange={(e) => set("max_uses", e.target.value)}
              placeholder="Sin límite"
            />
          </Field>
          <Field
            label="Usos por cliente"
            hint="Se cuenta por el celular del comprador."
            error={errores.max_uses_per_customer}
          >
            <Input
              inputMode="numeric"
              value={b.max_uses_per_customer}
              onChange={(e) => set("max_uses_per_customer", e.target.value)}
            />
          </Field>
        </FormGrid>

        <Field label="Descripción" hint="Para ti. No se le muestra al comprador.">
          <Textarea
            rows={2}
            value={b.description}
            onChange={(e) => set("description", e.target.value)}
            placeholder="Campaña de lanzamiento"
          />
        </Field>

        <Toggle
          label="Activo"
          hint="Si lo desactivas, deja de funcionar en el checkout al instante."
          checked={b.active}
          onChange={(v) => set("active", v)}
        />
      </div>
    </Modal>
  );
}
